import * as $ from "../jquery";
import KeyMap, { calculateHash, Schema, ShortcutMap } from "../util/keyMap";

/** JQueryKeyEvent handler function */
export type KeyEventHandler = (event: JQueryKeyEventObject) => boolean;

/**
 * Stack-based keyboard event handler registry.
 */
export class PliKeys {

    // -------------------------------------------------------------------------
    // Public
    // -------------------------------------------------------------------------

    /**
     * Creates an instance of PliKeys.
     */
    constructor(connector?: (handler: KeyEventHandler) => void) {
        if (typeof connector === "function") {
            this.connectEventHandler(connector);
        }
    }

    /**
     * Set the specified keymap as the first keymap that gets chance to handle keyboard events.
     *
     * If there are other activated keymaps, they will have their
     * chance to handle keyboard events after currently activated
     * keymap (i.e. in most-recently-activated order). If you activate
     * a keymap with special ID of null, a 'stop processing older keymaps'
     * token will be added to queue of activated keymaps. This token
     * can be removed by deactivating keymap with special ID of null.
     *
     * null is a separator that is meant for child forms. Consider the following
     * activation stack:
     *
     * keymap: formB
     * keymap: null
     * keymap: formA-grid
     * keymap: formA
     * keymap: default
     *
     * A keypress event would be checked against formB and then default. Once formB
     * is closed (e.g., modal popup dismissed), formB and null will be popped from
     * the stack via deactivateKeymap. Key events would then be checked against
     * formA-grid, then formA, and then default.
     *
     * @param id
     *     Keymap identifier
     */
    activateKeymap(id: string): void {
        let keymap: KeyMap<any>;
        if (typeof id !== "string" && id !== null) { throw new Error("Keymap ID must be a non-empty string or null"); }
        if (id) {
            keymap = this._keymaps[id];
            if (!keymap) { throw new Error(`Keymap ID "${id}" has not been registered`); }
        } else {
            keymap = null;
        }
        this._activated.push(keymap);
    }

    /**
     * Connects the event handler to an event source.
     *
     * @param connector
     *     Function that will receive the key event handler and connect it to the key event source.
     */
    connectEventHandler(connector: (handler: KeyEventHandler) => void): void {
        if (typeof connector !== "function") { throw new Error("Event handler connector must be a function"); }
        connector(e => this._handleKeydown(e));
    }

    /**
     * Stop handling keyboard events with the specified keymap.
     *
     * Only most-recently-activated keymap can be deactivated
     * (i.e. keymaps must be deactivated in most-recently-activated
     * order). If you deactivate a keymap with special ID of null,
     * a 'stop processing older keymaps' token will be removed
     * from queue of activated keymaps.
     *
     * @param id
     *     Keymap identifier.
     */
    deactivateKeymap(id: string): void {
        if (typeof id !== "string" && id !== null) { throw new Error("Keymap ID must be a non-empty string or null"); }
        if (this._activated.length === 0) { throw new Error("No keymaps are currently activated, unable to deactivate"); }
        const keymap = (id ? this._keymaps[id] : null);
        if (this._activated[this._activated.length - 1] !== keymap) { throw new Error("The keymap you're trying to deactivate is different from the most-recently-activated keymap"); }
        this._activated.pop();
    }

    /**
     * Returns true if the specified keymap has been registered.
     *
     * @returns
     *     True if the keymap has been registered using registerKeymap; false otherwise.
     */
    isKeymapRegistered(id: string): boolean {
        if (typeof id !== "string") { throw new Error("Keymap ID must be a non-empty string"); }
        return this._keymaps.hasOwnProperty(id);
    }

    /**
     * Register keymap for possible activation.
     *
     * @param id
     *     Keymap identifier.
     * @param shortcutMap
     *     A map of shortcut names to the actions they will perform.
     */
    registerKeymap<TAction>(id: string, shortcutMap: ShortcutMap<TAction>) : void {
        if (typeof id !== "string") { throw new Error("Keymap ID must be a non-empty string"); }
        if (typeof shortcutMap !== "object" || shortcutMap === null) { throw new Error("ShortcutMap must be a non-null object"); }
        if (this._keymaps.hasOwnProperty(id)) { throw new Error(`Keymap ID "${id}" has already been registered`); }
        this._keymaps[id] = new KeyMap(shortcutMap, id, this._schema);
    }

    /**
     * Set current schema to the specified value.
     *
     * A schema is a map that translates symbolic names to concrete
     * shortcuts. Setting schema to an invalid value (e.g. undefined or null
     * or any non-object value) will reset schema to empty schema.
     *
     * @example
     * {
     *     "save": "ctrl+s, command+s",
     *     "open": "ctrl+o, command+o"
     * }
     *
     * @param schema
     *     Schema to set globally and in each keymap.
     */
    setSchema(schema: Schema): void {
        this._schema = {};
        if (typeof schema === "object" && schema !== null) { // convert the specified schema object to a new object with all-lowercase keys
            for (const k in schema) {
                if (schema.hasOwnProperty(k)) {
                    this._schema[k.toString().toLowerCase()] = schema[k];
                }
            }
        }
        for (const k in this._keymaps) {
            if (this._keymaps.hasOwnProperty(k)) {
                this._keymaps[k].setSchema(this._schema);
            }
        }
    }

    /**
     * Unregister previously registered keymap.
     *
     * Keymaps can only be unregistered if they are currently not activated.
     *
     * @param id
     *     Keymap identifier.
     */
    unregisterKeymap(id: string): void {
        if (typeof id !== "string") { throw new Error("Keymap ID must be a non-empty string"); }
        if (!this._keymaps.hasOwnProperty(id)) { throw new Error(`Keymap ID "${id}" has not been registered`); }
        const keymap = this._keymaps[id];
        for (const activated of this._activated) {
            if (activated === keymap) {
                throw new Error(`Keymap ID "${id}" must be deactivated before it can be unregistered`);
            }
        }
        delete this._keymaps[id];
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /** Schema for the current form (initially empty). */
    private _schema: Schema = {};

    /** Registered keymaps (initially empty). */
    private _keymaps: { [id: string]: KeyMap<any> } = {};

    /** Queue of activated keymaps. */
    private _activated: KeyMap<any>[] = [];

    /**
     * Keydown event handler.
     */
    private _handleKeydown(event: JQueryKeyEventObject): boolean {
        const hash = calculateHash(event);

        for (let i = this._activated.length - 1; i > -2; i -= 1) { // note we go down to -1 (as opposed to the typical 0) => this is to ensure default keymap is always included
            let keymap = this._activated[i];
            if (!keymap) {
                keymap = this._keymaps["default"];
                if (!keymap) { // there is no default keymap, we're done looking for handler
                    break;
                }
                i = -1; // we found a default keymap - set index so that next "for" loop iteration will break the loop
            }

            keymap.createLookup(); // if keymap's lookup is already created, will no nothing
            const handler = keymap.lookup[hash];
            const result = (handler === false ? false : (typeof handler === "function" ? handler(event) : null));
            if (result === false) {
                return false; // returning false from jQuery event handler will ensure that event default is prevented and propagation is stopped
            }
            if (event.isPropagationStopped()) { // if the handler did not return false but called stopPropagation() on the passed-in event, stop looking for handler
                break;
            }
        }
        return null;
    }

}

/**
 * Keydown event handler for older IE browsers.
 */
function handleIEKeydown(event: JQueryKeyEventObject, handler: KeyEventHandler): boolean {
    const $event = ($ as any).event.fix(event);
    const result = handler($event);
    if (result === false || $event.isDefaultPrevented()) {
        try {
            event.keyCode = 0;
        } catch (ignore) {
            // IE sometimes throws "Access Denied" error, ignore it
        }
    }
    return result;
}

/**
 * Default PliKeys::connectEventHandler connector for browser environments.
 */
export function defaultConnector(handler: KeyEventHandler): void {
    // disable help window popup in IE
    const windowAny = window as any;
    if (windowAny.onhelp !== undefined) { windowAny.onhelp = function (): boolean { return false; }; }

    // start listening to keyboard events
    const documentAny = document as any;
    if ((documentAny.documentMode || 100) < 11 && typeof documentAny.attachEvent === "function") {
        // for more info about why we're using "attachEvent" in IE10 and older, please see:
        // http://stackoverflow.com/questions/13443742/preventing-default-keystroke-actions-in-internet-explorer
        // https://connect.microsoft.com/IE/feedback/details/780963/preventing-default-behavior-of-keyboard-shortcuts
        documentAny.attachEvent("onkeydown", (e: JQueryKeyEventObject) => handleIEKeydown(e, handler));
    } else {
        $(document).keydown(handler);
    }
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms/keys.ts